"use client";
import clsx from "clsx";
import { DeliveryOptionsResponse } from "@repo/apis/commerce-deliveryoptions-v2";
import { Link } from "@repo/next-utils/router";
import * as Flex from "@repo/ui/flex";
import { MY_ACCOUNT_BASE_URL } from "@/constants";
import {
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_HEADING1,
  TYPOGRAPHY_SMALLPRINT,
  TYPOGRAPHY_LABEL3_SENTENCE,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_LINK2,
  TYPOGRAPHY_BODY2_SENTENCE,
} from "@/theme/topshop/typography/typography-constants";
import { smallBreakpoint } from "@/theme/viewports";
import classes from "./index.module.css";

const ASOS_STAFF_DELIVERY_ID = 25;

const hardcodedReturnsData = [
  {
    type: "Inpost",
  },
  {
    type: "Royal Mail",
  },
  {
    type: "Royal Mail - Parcel Collect",
    description: (
      <Flex.Container column gap="2xs">
        <p className={`${TYPOGRAPHY_SMALLPRINT} ${classes.smallPrint}`}>
          Return from the comfort of your home Monday to Saturday, book
          collections up to 5 days in advance.
        </p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://topshop.returns.returnsplatform.com/main/landing"
          className={TYPOGRAPHY_LINK2}
        >
          Create return
        </a>
      </Flex.Container>
    ),
  },
  {
    type: "Isle of Man Post",
  },
  {
    type: "Guernsey Post (Customers in Guernsey only)",
  },
  {
    type: "Jersey Post (Customers in Jersey only)",
  },
];

export const DeliveryAndReturnsContent = ({
  className,
  deliveryAndReturnsData,
  lang,
}: {
  className?: string;
  deliveryAndReturnsData: DeliveryOptionsResponse | null;
  lang: string;
}) => {
  if (!deliveryAndReturnsData) {
    return null;
  }

  const locale = (lang?.split(".")?.[0] || "en-gb").replace("_", "-");

  return (
    <Flex.Container
      column
      gap="xl"
      className={clsx(TYPOGRAPHY_BODY3_SENTENCE, className)}
    >
      <Flex.Container column>
        <h2 className={TYPOGRAPHY_HEADING1}>Delivery</h2>

        <Flex.Container column gap="2xs" className={classes.info}>
          <p>Delivery to the United Kingdom.</p>
          <p>
            Need more information? See our{" "}
            <Link
              href="/customer-care/delivery/where-is-my-order"
              className={TYPOGRAPHY_LINK2}
            >
              delivery FAQ
            </Link>
          </p>
        </Flex.Container>

        {deliveryAndReturnsData &&
          deliveryAndReturnsData.deliveryCountryOptions.map((item) => {
            const proposition = item.messages.find(
              (message) => message.deliveryMessageType === "Proposition",
            );
            const upgradeMessage = item.upgradePrice && item.upgradeThreshold;

            const propositionDate = proposition?.messageContext?.[0]
              ? new Date(proposition.messageContext[0]).toLocaleDateString(
                  locale,
                  {
                    weekday: "short",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )
              : null;

            if (item.deliveryOptionType.id === ASOS_STAFF_DELIVERY_ID) {
              return null;
            }

            return (
              <Flex.Container
                className={classes.row}
                columnBelowWidth={`${smallBreakpoint}px`}
                key={`${item.resourceId}`}
                columnGap="sm"
              >
                <Flex.Item grow={2}>
                  <h3 className={TYPOGRAPHY_HEADING3_BOLD}>
                    {item.deliveryOptionType.name}
                  </h3>
                  {proposition ? (
                    <time
                      dateTime={proposition.messageContext?.[0]}
                      className={clsx(
                        TYPOGRAPHY_BODY2_SENTENCE,
                        classes.smallPrint,
                      )}
                      suppressHydrationWarning
                    >
                      {propositionDate
                        ? proposition.message.replace("{0}", propositionDate)
                        : proposition.message}
                    </time>
                  ) : null}
                </Flex.Item>
                <Flex.Item grow={1} className={classes.priceContainer}>
                  <span
                    className={`${TYPOGRAPHY_LABEL3_SENTENCE} ${classes.price}`}
                  >
                    {item.price.text}
                  </span>

                  {upgradeMessage && (
                    <span
                      className={`${TYPOGRAPHY_SMALLPRINT} ${classes.smallPrint}`}
                    >
                      {item.upgradePrice &&
                        `${item.upgradePrice.text} over ${item.upgradeThreshold?.text}`}
                    </span>
                  )}
                </Flex.Item>
              </Flex.Container>
            );
          })}
      </Flex.Container>
      <Flex.Container column>
        <Flex.Container gap="sm" justify="space-between" align="center">
          <h2 className={TYPOGRAPHY_HEADING1}>Returns</h2>
          <span className={classes.freeReturns}>Free on qualifying orders</span>
        </Flex.Container>

        <Flex.Container column gap="2xs" className={classes.info}>
          <p>
            <a
              href={`${MY_ACCOUNT_BASE_URL}/orders`}
              className={TYPOGRAPHY_LINK2}
            >
              Create return
            </a>{" "}
            to start the return process.
          </p>
          <p>
            Read all about our{" "}
            <Link
              href="/customer-care/returns-refunds/our-returns-policy"
              className={TYPOGRAPHY_LINK2}
            >
              28-day returns policy
            </Link>
          </p>
          <p>
            Read more on how to create a return and send back to Topshop.{" "}
            <Link
              href="/customer-care/returns-refunds/how-can-i-return-my-items"
              className={TYPOGRAPHY_LINK2}
            >
              View Details
            </Link>
          </p>
        </Flex.Container>

        {hardcodedReturnsData.map((item) => {
          return (
            <Flex.Container className={classes.row} column key={item.type}>
              <h3 className={TYPOGRAPHY_HEADING3_BOLD}>{item.type}</h3>
              {item.description}
            </Flex.Container>
          );
        })}
      </Flex.Container>
      <br />
    </Flex.Container>
  );
};
